import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert } from '../../ui';
import { request } from '../../api/client';
import { LedgerStatement } from '../../components/LedgerStatement';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * The retailer's own statement of account — credit summary + a running ledger (debits = invoices,
 * credits = payments) with each row deep-linking to its order. Self-scoped via /retailers/me.
 */
export function MyAccountPage() {
  const { data: me, isLoading, isError } = useQuery({ queryKey: ['my-retailer'], queryFn: () => request<any>({ url: '/retailers/me' }) });
  const { data: ledger, isLoading: ledgerLoading } = useQuery({ queryKey: ['my-ledger'], queryFn: () => request<any[]>({ url: '/retailers/me/ledger' }), enabled: !!me });

  const limit = Number(me?.creditLimit) || 0;
  const out = Number(me?.outstandingBalance) || 0;
  const owed = Math.max(0, out);
  const advance = Math.max(0, -out);
  const available = limit - out;
  // One running balance → one figure: positive = they owe (payable), negative = advance in their credit.
  const owes = out > 0.01, inCredit = out < -0.01;
  const balState = owes ? 'payable' : inCredit ? 'in credit' : 'settled';

  const totals = useMemo(() => {
    let billed = 0, paid = 0;
    for (const e of ledger ?? []) { billed += Number(e.debit || 0); paid += Number(e.credit || 0); }
    return { billed, paid, count: ledger?.length ?? 0 };
  }, [ledger]);

  if (isError) return <Alert type="info" title="No account statement is available for this login." />;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-content">My Account</h1>
        {me && <div className="text-sm text-muted">{me.companyName} · {me.code}</div>}
      </div>

      {/* Status banner */}
      {me && (
        advance > 0 ? (
          <div className="rounded-card border border-success-500/20 bg-success-500/10 px-4 py-3 text-sm text-success-600">
            <span className="font-semibold">You're {inr2(advance)} in credit (advance).</span> {inr(available)} available to place new orders.
          </div>
        ) : owed > 0 ? (
          <div className="rounded-card border border-warning-500/20 bg-warning-500/10 px-4 py-3 text-sm text-warning-600">
            <span className="font-semibold">You owe {inr2(owed)}.</span> {inr(available)} of your {inr(limit)} credit limit is still available.
          </div>
        ) : (
          <div className="rounded-card border border-line bg-surface-2 px-4 py-3 text-sm text-muted">All settled — nothing outstanding.</div>
        )
      )}

      {/* KPIs — a single Account balance tile: one figure whose colour + suffix show whether it's owed or in credit. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Kpi title="Credit limit" value={inr(limit)} accent="border-l-sky-500" />
        <Kpi
          title={inCredit ? 'In credit' : 'Outstanding'}
          value={<>{inr(Math.abs(out))}<span className="ml-1 text-xs font-normal text-muted">{balState}</span></>}
          accent={owes ? 'border-l-danger-500' : 'border-l-success-500'}
          valueCls={owes ? 'text-danger-600' : inCredit ? 'text-success-600' : 'text-content'}
        />
        <Kpi title="Available to order" value={inr(available)} accent={available > 0 ? 'border-l-brand-600' : 'border-l-danger-500'} valueCls={available > 0 ? 'text-brand-700' : 'text-danger-600'} />
      </div>

      {/* Statement */}
      <div className="overflow-hidden rounded-card border border-line bg-surface">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
          <h2 className="text-base font-semibold text-content">Statement of account</h2>
          <div className="text-xs text-muted">
            Billed <span className="font-semibold text-danger-600">{inr2(totals.billed)}</span> · Paid <span className="font-semibold text-success-600">{inr2(totals.paid)}</span> · {totals.count} entr{totals.count === 1 ? 'y' : 'ies'}
          </div>
        </div>
        {/* The account holder's OWN statement → mirror the frame to their side (payable / in credit). */}
        <LedgerStatement data={ledger ?? []} loading={isLoading || ledgerLoading} perspective="retailer" />
      </div>
    </div>
  );
}

function Kpi({ title, value, accent, valueCls }: { title: string; value: React.ReactNode; accent: string; valueCls?: string }) {
  return (
    <div className={`rounded-card border border-line border-l-[3px] ${accent} bg-surface px-4 py-3`}>
      <div className="text-xs text-muted">{title}</div>
      <div className={`mt-0.5 text-2xl font-bold ${valueCls ?? 'text-content'}`}>{value}</div>
    </div>
  );
}
